/**
 * taskflow-export-import.ts
 *
 * Lossless JSON and CSV Data Serialization and Ingestion Engine for TaskFlow.
 * Handles deduplication, schema validation, and safe error handling.
 */

import type { TaskFlowItem, TaskFlowItemType } from '../contracts/taskflow-schema.js';
import { validateTaskFlowItem } from '../contracts/taskflow-schema.js';

export interface TaskFlowExportPayload {
  version: '1.0';
  exportedAt: string;
  workspaceId: string;
  itemCount: number;
  items: TaskFlowItem[];
}

export class TaskFlowDataPortability {
  /**
   * Export items to formatted JSON string
   */
  static exportToJson(items: TaskFlowItem[], workspaceId: string): string {
    const payload: TaskFlowExportPayload = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      workspaceId,
      itemCount: items.length,
      items,
    };
    return JSON.stringify(payload, null, 2);
  }

  /**
   * Import items from JSON payload
   */
  static importFromJson(
    rawJson: string
  ): { success: true; items: TaskFlowItem[] } | { success: false; error: string } {
    try {
      const parsed = JSON.parse(rawJson);
      const rawList: unknown[] = Array.isArray(parsed) ? parsed : parsed.items;

      if (!Array.isArray(rawList)) {
        return { success: false, error: 'JSON does not contain a valid array of items' };
      }

      const validItems: TaskFlowItem[] = [];
      for (const raw of rawList) {
        const val = validateTaskFlowItem(raw);
        if (val.success) {
          validItems.push(val.data);
        }
      }

      if (validItems.length === 0 && rawList.length > 0) {
        return { success: false, error: 'No items passed schema validation' };
      }

      return { success: true, items: validItems };
    } catch (err: any) {
      return { success: false, error: `JSON Parse error: ${err?.message || 'Invalid syntax'}` };
    }
  }

  /**
   * Export items to CSV string
   */
  static exportToCsv(items: TaskFlowItem[]): string {
    const headers = [
      'id',
      'type',
      'title',
      'status',
      'priority',
      'startDate',
      'dueDate',
      'progressPercentage',
      'tags',
      'parent',
    ];

    const rows = [headers.join(',')];

    for (const item of items) {
      const titleEsc = `"${(item.title || '').replace(/"/g, '""')}"`;
      const tagsEsc = item.tags && item.tags.length > 0 ? `"${item.tags.join(';')}"` : '';

      const row = [
        item.id,
        item.type,
        titleEsc,
        item.status,
        item.priority || 'normal',
        item.startDate || '',
        item.dueDate || '',
        item.progressPercentage ?? 0,
        tagsEsc,
        item.parent || '',
      ];

      rows.push(row.join(','));
    }

    return rows.join('\r\n');
  }

  /**
   * Import items from CSV string
   */
  static importFromCsv(
    rawCsv: string
  ): { success: true; items: TaskFlowItem[] } | { success: false; error: string } {
    const trimmed = (rawCsv || '').trim();
    if (!trimmed) {
      return { success: false, error: 'CSV content is empty' };
    }

    try {
      const lines = trimmed.split(/\r\n|\n/);
      if (lines.length <= 1) {
        return { success: false, error: 'CSV does not contain data rows' };
      }

      const headers = lines[0].split(',').map((h) => h.trim());
      const items: TaskFlowItem[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Simple CSV row parser handling quotes
        const tokens: string[] = [];
        let cur = '';
        let inQuotes = false;

        for (let c = 0; c < line.length; c++) {
          const char = line[c];
          if (char === '"') {
            if (inQuotes && line[c + 1] === '"') {
              cur += '"';
              c++;
            } else {
              inQuotes = !inQuotes;
            }
          } else if (char === ',' && !inQuotes) {
            tokens.push(cur.trim());
            cur = '';
          } else {
            cur += char;
          }
        }
        tokens.push(cur.trim());

        const id = tokens[0] || `task-${Date.now()}-${i}`;
        const type = (tokens[1] || 'task') as TaskFlowItemType;
        const title = tokens[2] || 'Imported Task';
        const status = tokens[3] || 'backlog';
        const priority = (tokens[4] || 'normal') as any;
        const startDate = tokens[5] || undefined;
        const dueDate = tokens[6] || undefined;
        const progressPercentage = parseInt(tokens[7] || '0', 10) || 0;
        const tags = tokens[8] ? tokens[8].split(';') : [];
        const parent = tokens[9] || undefined;

        const val = validateTaskFlowItem({
          id,
          type,
          title,
          status,
          priority,
          startDate,
          dueDate,
          progressPercentage,
          tags,
          parent,
        });

        if (val.success) {
          items.push(val.data);
        }
      }

      return { success: true, items };
    } catch (err: any) {
      return { success: false, error: `CSV Parse error: ${err?.message || 'Invalid format'}` };
    }
  }
}
